/**
 * GTM → portal_activity_events bridge.
 *
 * Persists every consent-allowed GTM event into the database so the marketing
 * dashboards can render real numbers without depending on GA4. Uses a
 * fire-and-forget micro-batcher so the UI is never blocked.
 */
import { supabase } from "@/integrations/supabase/client";

type Row = {
  event_type: string;
  module: string;
  user_type: "anonymous";
  page_path: string | null;
  city: string | null;
  entity_type: string | null;
  entity_id: string | null;
  metadata: Record<string, unknown>;
  created_at?: string;
};

const PII_RE = /[\w.+-]+@[\w-]+\.[\w.-]+|\+?\d[\d\s().-]{6,}\d/g;
const SAFE_BLOCK = /password|token|secret|message|note|body|cover_letter|resume|email|phone|address|name(?!_)/i;

function safe(meta: Record<string, unknown> | undefined): Record<string, unknown> {
  if (!meta) return {};
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(meta)) {
    if (SAFE_BLOCK.test(k)) continue;
    if (v == null) continue;
    if (typeof v === "string") {
      const cleaned = v.replace(PII_RE, "[redacted]").slice(0, 200);
      if (cleaned) out[k] = cleaned;
    } else {
      out[k] = v;
    }
  }
  return out;
}

function detectCity(pathname: string, location?: string): string | null {
  const p = (pathname || "").toLowerCase();
  const loc = (location || "").toLowerCase();
  if (p.includes("oakland") || loc.includes("oakland")) return "Oakland";
  if (p.includes("san-jose") || p.includes("sanjose") || loc.includes("sanjose") || loc.includes("san_jose"))
    return "San José";
  return null;
}

/**
 * Map GTM event names → DB event_type. Unknown events fall through as-is so
 * marketing can still see them in the activity feed.
 */
const EVENT_MAP: Record<string, { event_type: string; entity_type?: string }> = {
  cta_click: { event_type: "public_cta_clicked", entity_type: "cta" },
  nav_click: { event_type: "public_nav_clicked", entity_type: "nav" },
  phone_click: { event_type: "public_contact_click", entity_type: "contact" },
  email_click: { event_type: "public_contact_click", entity_type: "contact" },
  outbound_click: { event_type: "public_outbound_click", entity_type: "outbound" },
  file_download: { event_type: "public_file_download", entity_type: "download" },
  dialog_open: { event_type: "public_dialog_open", entity_type: "dialog" },
  dialog_close: { event_type: "public_dialog_closed", entity_type: "dialog" },
  form_start: { event_type: "public_form_started", entity_type: "form" },
  form_submit_attempt: { event_type: "public_form_attempt", entity_type: "form" },
  form_submit_success: { event_type: "public_form_submitted", entity_type: "form" },
  form_submit_error: { event_type: "public_form_error", entity_type: "form" },
  scroll_depth: { event_type: "public_scroll_depth", entity_type: "engagement" },
  page_view: { event_type: "public_page_viewed", entity_type: "page" },
};

const SAMPLED = new Set<string>(["scroll_depth", "nav_click"]);
const dedupe = new Map<string, number>(); // key -> ts ms
const DEDUPE_WINDOW = 1500;

let buffer: Row[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

async function flush() {
  flushTimer = null;
  if (buffer.length === 0) return;
  const rows = buffer;
  buffer = [];
  try {
    await supabase.from("portal_activity_events").insert(rows as any);
  } catch {
    /* swallow */
  }
}

function schedule(row: Row, immediate: boolean) {
  buffer.push(row);
  if (immediate) {
    if (flushTimer) clearTimeout(flushTimer);
    void flush();
    return;
  }
  if (!flushTimer) flushTimer = setTimeout(flush, 4000);
  if (buffer.length >= 20) {
    if (flushTimer) clearTimeout(flushTimer);
    void flush();
  }
}

if (typeof window !== "undefined") {
  window.addEventListener("pagehide", () => void flush());
  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") void flush();
  });
}

/**
 * Persist one GTM event. Called from src/lib/gtm.ts pushRaw().
 */
export function persistGtmEvent(event: string, params: Record<string, unknown>): void {
  if (typeof window === "undefined") return;
  const path = (params.page_path as string) || window.location.pathname;
  if (!path) return;
  if (path.startsWith("/cws-admin") || path.startsWith("/dashboard") || path.startsWith("/admin")) return;

  const map = EVENT_MAP[event] ?? { event_type: `public_${event}`, entity_type: "custom" };

  // Dedupe within a short window: same event + path + gtm_id + cta_text bucket.
  const dedupeKey = [event, path, params.gtm_id ?? "", params.cta_text ?? "", params.percent ?? ""].join("|");
  const now = Date.now();
  const last = dedupe.get(dedupeKey);
  if (last && now - last < DEDUPE_WINDOW) return;
  dedupe.set(dedupeKey, now);
  // Trim cache occasionally
  if (dedupe.size > 200) {
    const cutoff = now - 30_000;
    for (const [k, v] of dedupe) if (v < cutoff) dedupe.delete(k);
  }

  // Channel for contact clicks
  let channel: string | undefined;
  if (event === "phone_click") channel = "phone";
  else if (event === "email_click") channel = "email";

  // Service hint
  const lowerPath = path.toLowerCase();
  let service: string | undefined;
  if (lowerPath.includes("commercial")) service = "commercial";
  else if (lowerPath.includes("careers")) service = "careers";

  const row: Row = {
    event_type: map.event_type,
    module: "public_site",
    user_type: "anonymous",
    page_path: path.slice(0, 200),
    city: detectCity(path, params.gtm_location as string | undefined),
    entity_type: map.entity_type ?? null,
    entity_id: (params.gtm_id as string | undefined) ?? null,
    metadata: safe({
      gtm_event: event,
      gtm_id: params.gtm_id,
      gtm_location: params.gtm_location,
      gtm_type: params.gtm_type,
      element_type: params.element_type,
      cta_label: params.cta_text,
      target_path: params.link_url,
      section: params.section,
      device: params.device,
      percent: params.percent,
      form_id: params.form_id,
      form_location: params.form_location,
      dialog_id: params.dialog_id,
      channel,
      service,
    }),
  };

  schedule(row, !SAMPLED.has(event));
}
